import type { AnimationEvent } from '@angular/animations';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { OverlayContainer } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  model,
  Output,
  output,
  reflectComponentType,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { merge, combineLatest, EMPTY, fromEvent, timer, Observable } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { CollapseState, observeMutations } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  DialogService,
  HotkeysService,
  IconComponent,
  KeyCode,
  ScrollableDirective,
  DropdownComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { SidebarInternalService } from './sidebar-internal.service';

export const collapseHorizontal = trigger('collapseHorizontal', [
  state(
    'collapsed',
    style({
      width: '{{collapsedSize}}px',
      minWidth: '{{collapsedSize}}px',
    }),
    { params: { collapsedSize: 48 } },
  ),
  state(
    'normal',
    style({
      width: '*',
      minWidth: '*',
    }),
  ),
  // immediate change state from none state to collapsed
  transition('void <=> collapsed', animate('0ms')),
  transition('* <=> collapsed', animate('200ms cubic-bezier(0.16, 1, 0.3, 1)')),
  transition('collapsed <=> normal', animate('200ms cubic-bezier(0.16, 1, 0.3, 1)')),
]);

interface SidebarState {
  isHover: boolean;
  isMinimized: boolean;
  isExpanded: boolean;
  collapseState: { value: CollapseState; params: { collapsedSize: number } };
}

@Component({
  selector: 'rt-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.less'],
  providers: [SidebarInternalService],
  animations: [collapseHorizontal],
  imports: [ScrollableDirective, TranslatePipe, IconComponent],
  host: {
    '[class.expanded]': 'state().isExpanded',
    '[class.overlay]': 'state().isHover',
    '[class.minimized]': 'state().isMinimized',
    '[class.large]': 'size() === "large"',
    '[class.padding-large]': 'paddingSize() === "large"',
    '[@collapseHorizontal]': 'state().collapseState',
    '(@collapseHorizontal.start)': 'animationEvent.emit($event)',
    '(@collapseHorizontal.done)': 'animationEvent.emit($event)',
  },
})
export class SidebarComponent {
  private readonly sidebarInternalService = inject(SidebarInternalService);
  private readonly hotkeysService = inject(HotkeysService);
  private readonly dialogService = inject(DialogService);
  private readonly elementRef = inject(ElementRef);

  private readonly overlayContainer = inject(OverlayContainer);

  public isExpanded = model(true);
  public hasExpander = input(true);
  public enableExpanderShortcuts = input(true);
  public hasBorderRadius = input(true);
  public size = input<'default' | 'large'>('default');
  public paddingSize = input<'default' | 'large'>('default');
  public lockState = input(false);

  /**
   * output() will throw an error if event is emitted after the component is destroyed
   * Angular animations emit done event after the component is destroyed
   * see https://github.com/angular/angular/issues/19826 and replace with output() when fixed
   */
  @Output() public animationEvent = new EventEmitter<AnimationEvent>();
  public expanded = output<boolean>();

  public isMinimized = computed(() => {
    const snapshot = this.stateSnapshot();
    return this.lockState() && snapshot
      ? snapshot.isMinimized
      : !this.isExpanded() && !this.isHover();
  });

  protected state = computed((): SidebarState => {
    const snapshot = this.stateSnapshot();
    return this.lockState() && snapshot
      ? snapshot
      : {
          isHover: this.isHover(),
          isMinimized: this.isMinimized(),
          isExpanded: this.isExpanded(),
          collapseState: this.collapseState(),
        };
  });

  private stateSnapshot = signal<SidebarState | null>(null);
  private isHover = signal(false);
  private collapseState = computed(() => ({
    value: this.isExpanded() ? CollapseState.Normal : CollapseState.Collapsed,
    params: { collapsedSize: this.paddingSize() === 'large' ? 56 : 48 },
  }));

  private untilDestroyed = injectUntilDestroyed();

  constructor() {
    this.watchLockState();
    this.watchIsMinimized();
    this.watchHotkeys();
    this.debounceHover();
  }

  public toggleSidebar(expand: boolean): void {
    this.isExpanded.set(expand);
    this.isHover.set(false);
    this.expanded.emit(expand);
  }

  private watchHotkeys(): void {
    combineLatest([toObservable(this.hasExpander), toObservable(this.enableExpanderShortcuts)])
      .pipe(
        switchMap(([hasExpander, enableExpanderShortcuts]) =>
          hasExpander && enableExpanderShortcuts
            ? merge(
                this.getKeyStream(KeyCode.BracketLeft, false),
                this.getKeyStream(KeyCode.BracketRight, true),
              )
            : EMPTY,
        ),
        this.untilDestroyed,
      )
      .subscribe(expand => this.handleHotkeyEvent(expand));
  }

  private watchIsMinimized(): void {
    effect(() => this.sidebarInternalService.sidebarMinimized.next(this.isMinimized()));
  }

  private handleHotkeyEvent(expand: boolean): void {
    if (this.dialogService.hasDialogsOpen() || expand === this.isExpanded()) return;

    this.toggleSidebar(expand);
  }

  private getKeyStream<T>(key: KeyCode, mapTo: T): Observable<T> {
    return this.hotkeysService.shortcut([key], true, false).pipe(map(() => mapTo));
  }

  private debounceHover() {
    const timeHoverForce = 150;
    const timeHoverPointerStop = 50;

    const enter$ = fromEvent(this.elementRef.nativeElement, 'mouseenter');
    const leave$ = fromEvent(this.elementRef.nativeElement, 'mouseleave');
    const move$ = fromEvent(this.elementRef.nativeElement, 'mousemove');

    const observeOverlayContainer$ = observeMutations(this.overlayContainer.getContainerElement(), {
      childList: true,
      subtree: true,
      attributes: false,
    }).pipe(switchMap(() => (this.getOpenDropdowns() > 0 ? EMPTY : [false])));

    merge(
      enter$.pipe(
        switchMap(() =>
          merge(
            move$.pipe(startWith(null), debounceTime(timeHoverPointerStop)),
            timer(timeHoverForce),
          ).pipe(
            map(() => true),
            take(1),
            takeUntil(leave$),
          ),
        ),
      ),
      leave$.pipe(
        debounceTime(timeHoverPointerStop),
        switchMap(() => {
          if (this.getOpenDropdowns() > 0) return observeOverlayContainer$;
          return [false];
        }),
      ),
    )
      .pipe(this.untilDestroyed)
      .subscribe(value => this.isHover.set(value));
  }

  private getOpenDropdowns() {
    const dropdownSelector = reflectComponentType(DropdownComponent)!.selector;
    return (
      this.overlayContainer.getContainerElement().querySelectorAll(dropdownSelector)?.length || 0
    );
  }

  private watchLockState() {
    combineLatest({
      isHover: toObservable(this.isHover),
      isMinimized: toObservable(this.isMinimized),
      isExpanded: toObservable(this.isExpanded),
      collapseState: toObservable(this.collapseState),
    })
      .pipe(
        // Debounce is required to get state before hover change
        debounceTime(0),
        filter(() => !this.lockState()),
        this.untilDestroyed,
      )
      .subscribe(snapshot => this.stateSnapshot.set(snapshot));
  }
}
